import {observer} from "mobx-react";
import React from "react";
import {useBetween} from "use-between";
import FormClients from "@dropDesk/presentation/pages/client/ui/my_clients/form_client";
import {ContentFormCreate} from "@dropDesk/presentation/pages/ticket/ui/form_create/modals/styled";
import ModalMain from "@dropDesk/presentation/components/modals/modal_main";
import {useModalChat} from "@dropDesk/presentation/pages/chat/ui/contact_list/modals/handle_change_visible";
import ModalInfoNewTicket from "@dropDesk/presentation/components/modals/modal_information";
import {BackendAction} from "@dropDesk/domain/entities/common/actions_entity";
import FormCreateTicket from "@dropDesk/presentation/pages/ticket/ui/form_create";
import {TicketEntity} from "@dropDesk/domain/entities/ticket/ticket.entity";
import {ICONS_DROPDESK} from "@dropDesk/storage/icons/icons.d";
import {DropDeskErrorEnum} from "@dropDesk/domain/entities/exceptions/dropdesk_error_enum";

const ModalsChat = observer(
    ({
         handleSubmitEditClient,
         handleSubmitNewChat
     }: {
        handleSubmitEditClient: () => void
        handleSubmitNewChat: (ticket: TicketEntity) => void

    }) => {

        const {
            visibleModalEditClient, setVisibleModalEditClient,
            visibleModalInfoNewTicket, setVisibleModalInfoNewTicket,
            visibleModalNewTicket, setVisibleModalNewTicket,
            visibleModalContactWithoutTicket, setVisibleModalContactWithoutTicket,
            visibleModalContactInAttendance, setVisibleModalContactInAttendance
        } = useBetween(useModalChat);

        const closeModalNewTicket = () => {
            setVisibleModalNewTicket(false);
            setVisibleModalInfoNewTicket({
                visible: false,
                client: null,
                contact: null
            });
        };

        const closeModalContactInAttendance = () => {
            setVisibleModalContactInAttendance({
                visible: false,
                number: null,
                sector: null,
                attendant: null
            });
        };

        const handleErrorCreateTicket = (err: any): boolean => {
            if (err?.message !== DropDeskErrorEnum.contactInAttendance) {
                return false;
            }
            closeModalNewTicket();
            setVisibleModalContactInAttendance({
                visible: true,
                number: err?.extra?.number ?? null,
                sector: err?.extra?.sector?.name ?? null,
                attendant: err?.extra?.attendant?.name ?? null
            });
            return true;
        };

        const textContactInAttendance = (): React.ReactNode => {
            const {number, sector, attendant} = visibleModalContactInAttendance;
            return (
                <>
                    Este contato já possui um atendimento em aberto
                    {number ? <> (nº <b>{number}</b>)</> : ''}
                    {sector ? <>, no setor <b>{sector}</b></> : ''}
                    {attendant ? <>, com o atendente <b>{attendant}</b></> : ''}
                    . O DropDesk não permite dois atendimentos simultâneos para o mesmo contato,{' '}
                    por isso não é possível abrir um novo agora.{' '}
                    Se este atendimento não aparece nas suas listas, ele está em um setor fora da sua visualização:{' '}
                    peça ao responsável para finalizá-lo ou transferi-lo para o seu setor.
                </>
            );
        };

        return (
            <>
                <ModalMain
                    open={visibleModalEditClient.visible}
                    onRequestClose={() => setVisibleModalEditClient({visible: false, idClient: null})}
                    width={1060}
                    renderComponent={
                        <ContentFormCreate>
                            <FormClients
                                isEdit={true}
                                isNewChatContact={!visibleModalEditClient.idClient}
                                idClientExternal={visibleModalEditClient.idClient}
                                handleSubmitExternalScreen={(client) => {
                                    handleSubmitEditClient();
                                    const isCreateNewClient = !visibleModalEditClient.idClient;
                                    if (isCreateNewClient) {
                                        const hasOnlyOneContact: boolean = client.users?.filter((entry) => entry.action !== BackendAction.delete).length === 1;
                                        setVisibleModalInfoNewTicket({
                                            visible: true,
                                            client: client,
                                            contact: hasOnlyOneContact ? client.users![0] : null
                                        });
                                    }
                                }}
                                backPreviousScreenExternalScreen={() => setVisibleModalEditClient({
                                    visible: false,
                                    idClient: null
                                })}
                            />
                        </ContentFormCreate>}
                />

                {visibleModalInfoNewTicket.client && <ModalMain
                    open={visibleModalNewTicket}
                    onRequestClose={() => closeModalNewTicket()}
                    width={1060}
                    renderComponent={
                        <ContentFormCreate>
                            <FormCreateTicket
                                isNewChat={true}
                                isNewChatBody={{
                                    client: visibleModalInfoNewTicket.client,
                                    contact: visibleModalInfoNewTicket.contact,
                                }}
                                handleSubmitExternalScreen={(ticket) => {
                                    handleSubmitNewChat(ticket);
                                    setVisibleModalInfoNewTicket({
                                        visible: false,
                                        client: null,
                                        contact: null
                                    });
                                }}
                                handleErrorExternalScreen={handleErrorCreateTicket}
                                backPreviousScreenExternalScreen={() => closeModalNewTicket()}
                            />
                        </ContentFormCreate>
                    }
                />}

                <ModalInfoNewTicket
                    open={visibleModalInfoNewTicket.visible}
                    onRequestClose={() => {
                        setVisibleModalInfoNewTicket({
                            visible: false,
                            client: null,
                            contact: null
                        });
                    }}
                    textHeaderModal={'Novo atendimento'}
                    textButton={'Confirmar'}
                    onRequestSave={() => {
                        setVisibleModalNewTicket(true);
                    }}
                    nameIcon={ICONS_DROPDESK.chat}
                    sizeIcon={130}
                    textInformation={
                        `Deseja abrir um novo atendimento para o cliente ${visibleModalInfoNewTicket.contact ? visibleModalInfoNewTicket.contact.name : visibleModalInfoNewTicket.client?.name}?`
                    }
                    loading={false}
                />

                <ModalInfoNewTicket
                    open={visibleModalContactWithoutTicket.visible}
                    onRequestClose={() => setVisibleModalContactWithoutTicket({visible: false, contact: null})}
                    textHeaderModal={'Nenhum atendimento encontrado'}
                    textButton={'Novo atendimento'}
                    onRequestSave={() => {
                        const contact = visibleModalContactWithoutTicket.contact;
                        setVisibleModalContactWithoutTicket({visible: false, contact: null});
                        if (contact) {
                            setVisibleModalInfoNewTicket({
                                visible: false,
                                client: contact.client!,
                                contact: contact
                            });
                            setVisibleModalNewTicket(true);
                        }
                    }}
                    nameIcon={ICONS_DROPDESK.emptyFolder}
                    sizeIcon={115}
                    textInformation={
                        `Este contato não possui nenhum atendimento no período recente de 90 dias. ` +
                        `Para consultar o histórico completo, acesse o menu Atendimentos. ` +
                        `Se preferir, abra um novo atendimento agora mesmo.`
                    }
                    loading={false}
                />

                <ModalInfoNewTicket
                    open={visibleModalContactInAttendance.visible}
                    onRequestClose={() => closeModalContactInAttendance()}
                    textHeaderModal={'Contato já em atendimento'}
                    textButton={'Entendi'}
                    onRequestSave={() => closeModalContactInAttendance()}
                    nameIcon={ICONS_DROPDESK.warningFill}
                    sizeIcon={115}
                    textInformation={textContactInAttendance()}
                    loading={false}
                    hideCloseButton={true}
                />
            </>
        )
    })
export default ModalsChat;
